import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import DeleteIcon from '@material-ui/icons/Delete'
import Fab from '@material-ui/core/Fab'
import AddIcon from '@material-ui/icons/Add'
import Modal from '@material-ui/core/Modal'
import { format } from 'date-fns'
import FormControl from '@material-ui/core/FormControl'
import Input from '@material-ui/core/Input'
import InputLabel from '@material-ui/core/InputLabel'
import Button from '@material-ui/core/Button'
import { Link } from 'react-router-dom'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import { computeGroupSyncStats, getSyncHealthColor, SYNC_WINDOW_DAYS } from './utils/syncStatus'
import AppShell from './components/layout/AppShell'
import StatusBadge from './components/ui/StatusBadge'
import EmptyState from './components/ui/EmptyState'

const styles = (theme) => ({
  legend: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  legendItems: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    columnGap: theme.spacing(4),
    rowGap: theme.spacing(4),
  },
  cardLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    height: '100%',
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 14px 32px rgba(15, 23, 42, 0.1)',
    },
  },
  cardContent: {
    flex: 1,
  },
  cardTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
  },
  cardDesc: {
    color: theme.palette.text.secondary,
    minHeight: 40,
    marginBottom: theme.spacing(1),
  },
  cardMeta: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2, 2),
    borderTop: '1px solid #eef2f7',
  },
  statItem: {
    flex: 1,
    minWidth: 0,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  statValue: {
    fontSize: '0.95rem',
    fontWeight: 600,
    margin: 0,
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
  },
  modal: {
    position: 'absolute',
    width: theme.spacing(50),
    maxWidth: '92vw',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    outline: 'none',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: 18,
  },
  submit: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(1),
  },
})

function healthTone(pct) {
  if (pct >= 80) return 'good'
  if (pct >= 50) return 'warn'
  return 'bad'
}

function healthLabel(pct) {
  if (pct >= 80) return '良好'
  if (pct >= 50) return '尚可'
  return '需關注'
}

const generateListItem = (groups, deleteGroup, classes) => {
  return groups.map((e) => {
    const stats = computeGroupSyncStats(e.all_users, e.spec_users)
    const healthColor = getSyncHealthColor(stats.syncedWithinPct)

    return (
      <Link key={e.uniqid} className={classes.cardLink} to={`/${e.uniqid}/patients`}>
        <Card className={classes.card} style={{ borderTop: `4px solid ${healthColor}` }}>
          <CardContent className={classes.cardContent}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <Typography variant="h6" color="primary" className={classes.cardTitle}>
                {e.name}
              </Typography>
              <StatusBadge label={healthLabel(stats.syncedWithinPct)} tone={healthTone(stats.syncedWithinPct)} />
            </div>
            <Typography variant="body2" className={classes.cardDesc}>
              {e.description || '尚無描述'}
            </Typography>
            <Typography className={classes.cardMeta}>
              {format(new Date(e.created_at), 'yyyy/MM/dd')}
            </Typography>
          </CardContent>
          <div className={classes.cardFooter}>
            <div className={classes.statItem}>
              <Typography className={classes.statLabel}>總人數</Typography>
              <Typography className={classes.statValue}>{stats.total} 人</Typography>
            </div>
            <div className={classes.statItem}>
              <Typography className={classes.statLabel}>{SYNC_WINDOW_DAYS} 天內同步</Typography>
              <Typography className={classes.statValue} style={{ color: healthColor }}>
                {stats.syncedWithin} 人（{stats.syncedWithinPct}%）
              </Typography>
            </div>
            <IconButton
              style={{ flexShrink: 0 }}
              aria-label="Delete"
              onClick={(event) => deleteGroup(event, e.name, e.uniqid)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        </Card>
      </Link>
    )
  })
}

class InteractiveList extends Component {
  state = {
    modalOpen: false,
    deleteDialog: false,
    deleteGroupName: '',
    deleteGroupID: '',
    groups: [],
  }

  async fetchGroups() {
    try {
      const result = await fetch(`/api/groups`, {
        mode: 'cors',
        method: 'GET',
      })

      const data = await result.json()

      if (data.success) {
        this.setState({ groups: data.data })
      }
    } catch (err) {
      this.props.history.push('/login')
    }
  }

  componentDidMount() {
    this.fetchGroups()
  }

  handleDeleteClose = () => {
    this.setState({ deleteDialog: false })
  }

  handleModalOpen = () => {
    this.setState({ modalOpen: true })
  }

  handleModalClose = () => {
    this.setState({ modalOpen: false })
  }

  deleteGroup = (e, name, uniqid) => {
    e.preventDefault()
    this.setState({
      deleteDialog: true,
      deleteGroupName: name,
      deleteGroupID: uniqid,
    })
  }

  render() {
    const { classes } = this.props

    return (
      <AppShell title="個案群組" subtitle="管理照護群組與同步健康狀態">
        <Typography variant="body2" className={classes.legend}>
          同步狀態以最近 {SYNC_WINDOW_DAYS} 天內是否有上傳資料為準
        </Typography>
        <div className={classes.legendItems}>
          <span style={{ color: getSyncHealthColor(80) }}>● 良好（≥80%）</span>
          <span style={{ color: getSyncHealthColor(65) }}>● 尚可（50–79%）</span>
          <span style={{ color: getSyncHealthColor(30) }}>● 需關注（&lt;50%）</span>
        </div>

        {this.state.groups.length === 0 ? (
          <EmptyState title="尚無群組" description="點右下角按鈕新增第一個個案群組" />
        ) : (
          <div className={classes.grid}>
            {generateListItem(this.state.groups, this.deleteGroup, classes)}
          </div>
        )}

        <Fab className={classes.fab} color="primary" onClick={this.handleModalOpen}>
          <AddIcon />
        </Fab>

        <Modal open={this.state.modalOpen} onClose={this.handleModalClose}>
          <div className={classes.modal}>
            <Typography variant="h6" id="modal-title">
              新增群組
            </Typography>
            <form method="post" action="/api/createGroup">
              <FormControl margin="normal" required fullWidth>
                <InputLabel htmlFor="name">群組名稱</InputLabel>
                <Input id="name" name="name" autoFocus />
              </FormControl>
              <FormControl margin="normal" required fullWidth>
                <InputLabel htmlFor="description">描述</InputLabel>
                <Input name="description" id="description" />
              </FormControl>
              <Button onClick={this.handleModalClose} className={classes.submit}>
                取消
              </Button>
              <Button type="submit" color="primary" variant="contained" className={classes.submit}>
                建立
              </Button>
            </form>
          </div>
        </Modal>

        <Dialog open={this.state.deleteDialog} onClose={this.handleDeleteClose}>
          <form method="post" action="/api/deleteGroup">
            <Input type="hidden" id="uniqid" name="uniqid" value={this.state.deleteGroupID} />
            <DialogTitle>確定要刪除群組【 {this.state.deleteGroupName}】</DialogTitle>
            <DialogContent>
              <DialogContentText>此動作為不可逆的操作！</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleDeleteClose} color="primary">
                取消
              </Button>
              <Button onClick={this.handleDeleteClose} color="primary" type="submit" autoFocus>
                確認
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </AppShell>
    )
  }
}

InteractiveList.propTypes = {
  classes: PropTypes.object.isRequired,
}

export default withStyles(styles)(InteractiveList)
